/**
 * Curator Bridge Slice-4 state.
 *
 * Query keys are namespaced under "curator" so the queue can be invalidated on
 * its own, and every accepted decision ALSO invalidates the "signals" namespace,
 * because a publication decision moves a signal into or out of the feed every
 * ordinary user reads.
 *
 * Nothing here retries automatically. The project disables query retries
 * globally (src/lib/queryClient.ts) and mutations do not retry by default; for a
 * consequential, idempotency-keyed decision that default is a requirement rather
 * than a convention. A retry is always an explicit act by the curator, and it
 * reuses the decision key only when the intent is genuinely unchanged.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  CURATOR_PAGE_SIZE,
  getPublicationContext,
  listCuratorQueue,
  submitCuratorDecision,
} from './api';
import {
  applyFailureToController,
  browserDecisionKey,
  createDecisionKeyController,
  type DecisionIntent,
  type SubmissionBlock,
} from './decision-intent';
import { CuratorApiError } from './errors';
import type {
  CuratorQueuePage,
  DecisionResult,
  ListableQueueState,
  PublicationContext,
} from './types';

export const curatorKeys = {
  all: ['curator'] as const,
  queue: (state: ListableQueueState) => ['curator', 'queue', state] as const,
};

/**
 * One page-walked view of a single withheld state.
 *
 * `useInfiniteQuery` matches the server's keyset cursor exactly: each page
 * carries the cursor for the next, so a queue larger than one page stays fully
 * traversable without repeating or skipping a row.
 */
export function useCuratorQueue(state: ListableQueueState) {
  const query = useInfiniteQuery<CuratorQueuePage>({
    queryKey: curatorKeys.queue(state),
    queryFn: ({ pageParam }) =>
      listCuratorQueue({
        publicationState: state,
        cursor: (pageParam as string | null) ?? null,
        limit: CURATOR_PAGE_SIZE,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    retry: false,
  });

  const rows = useMemo(
    () => query.data?.pages.flatMap((page) => page.rows) ?? [],
    [query.data],
  );

  return {
    rows,
    status: query.status,
    error: query.error instanceof CuratorApiError ? query.error : null,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: () => {
      void query.fetchNextPage();
    },
    refetch: () => query.refetch(),
  };
}

/**
 * The on-demand published-context read.
 *
 * A mutation rather than a query because it is imperative: it must fire when the
 * curator opens an unpublish control and at no other time. As a query it would
 * either run on mount for every viewer of every published signal - including
 * ordinary users, who would receive a 403 they never asked for - or need
 * `enabled: false` plumbing to prevent exactly that.
 */
export function usePublicationContext() {
  return useMutation<PublicationContext, CuratorApiError, string>({
    mutationFn: (signalId: string) => getPublicationContext(signalId),
    retry: false,
  });
}

export interface CuratorDecisionState {
  /**
   * Submits, and reports whether the controller ACCEPTED the attempt.
   *
   * The return value is load-bearing rather than informational: a queue shares
   * one controller across every row, so a caller must not record which row owns
   * the in-flight state until it knows an attempt actually started.
   */
  submit: (intent: DecisionIntent) => boolean;
  isPending: boolean;
  error: CuratorApiError | null;
  result: DecisionResult | null;
  /** A 409 is outstanding: authoritative state must be re-read before retrying. */
  isConflicted: boolean;
  /** Why submission is refused, or null when it is open. */
  blockReason: SubmissionBlock | null;
  /** True when the last failure may be retried with the SAME decision key. */
  canRetrySameKey: boolean;
  /** Call after re-reading state so the next submit is allowed (and re-keyed). */
  clearConflict: () => void;
  reset: () => void;
}

/**
 * One decision surface's mutation, owning the decision-key lifecycle.
 *
 * The controller lives in a ref and is touched ONLY from the submit handler and
 * the mutation callbacks - never during render. That is what keeps key identity
 * a property of the curator's actions rather than of React's render schedule: a
 * StrictMode double-render, a parent re-render or a Suspense replay cannot mint
 * a key or silently rotate one.
 */
export function useCuratorDecision(options?: {
  onSuccess?: (result: DecisionResult) => void;
}): CuratorDecisionState {
  const queryClient = useQueryClient();
  const controllerRef = useRef(createDecisionKeyController(browserDecisionKey));
  const [blockReason, setBlockReason] = useState<SubmissionBlock | null>(null);
  const onSuccess = options?.onSuccess;

  const mutation = useMutation<
    DecisionResult,
    CuratorApiError,
    { intent: DecisionIntent; decisionKey: string }
  >({
    mutationFn: ({ intent, decisionKey }) =>
      submitCuratorDecision(intent, decisionKey),
    retry: false,
    onSuccess: (result) => {
      // The signal moved. Refresh the curator queues AND the ordinary signals
      // surface, so an approved signal appears in the feed and an unpublished
      // one leaves it instead of lingering as a stale row.
      controllerRef.current.reset();
      setBlockReason(null);
      void queryClient.invalidateQueries({ queryKey: curatorKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['signals'] });
      onSuccess?.(result);
    },
    onError: (error) => {
      // The failure policy lives in one shared function, so the hook and its
      // tests exercise the same decision. A conflict blocks pending an
      // authoritative re-read; a refusal the server would repeat verbatim
      // blocks this exact intent; an uncertain failure blocks nothing and keeps
      // its key for an explicit retry.
      setBlockReason(applyFailureToController(controllerRef.current, error));
    },
    // Released however the request ended, so a failure does not wedge the
    // controller shut and block every later retry.
    onSettled: () => {
      controllerRef.current.settleAttempt();
    },
  });

  const submit = useCallback(
    (intent: DecisionIntent): boolean => {
      // One controller serves a whole queue, so the reservation is what keeps a
      // second row from starting while the first is still in flight, what
      // refuses a resend while a conflict stands - a stale expected version
      // could only reproduce the same 409 - and what refuses resending an
      // intent the server has already refused outright.
      const decisionKey = controllerRef.current.beginAttempt(intent);
      if (decisionKey === null) return false;
      mutation.mutate({ intent, decisionKey });
      return true;
    },
    [mutation],
  );

  const clearConflict = useCallback(() => {
    controllerRef.current.clearConflict();
    setBlockReason(null);
    mutation.reset();
  }, [mutation]);

  const reset = useCallback(() => {
    controllerRef.current.reset();
    setBlockReason(null);
    mutation.reset();
  }, [mutation]);

  return {
    submit,
    isPending: mutation.isPending,
    error: mutation.error ?? null,
    result: mutation.data ?? null,
    isConflicted: blockReason === 'conflict',
    blockReason,
    canRetrySameKey: mutation.error?.retryable === true,
    clearConflict,
    reset,
  };
}
